// :)
/**
 * synkro.lock — the trust anchor committed alongside synkro.toml.
 *
 * For every subscribed pack it pins the exact attested (version, digest,
 * signature, signing key). `synkro sync` resolves floating refs to a concrete
 * digest, verifies it, and records it here so the same bytes are reproducible
 * and a tampered re-resolve is detectable. Hand-rolled TOML writer/reader to
 * match the repo's dependency-free synkro.toml handling.
 */

import { existsSync, readFileSync, writeFileSync } from 'node:fs';
import { join } from 'node:path';

export interface LockEntry {
  ref: string;          // "namespace/name"
  version: string;
  digest: string;       // sha256:<hex>
  signature: string;    // Ed25519, base64
  signingKeyId: string;
}

const LOCK_FILE = 'synkro.lock';

export function lockPath(repoRoot: string): string {
  return join(repoRoot, LOCK_FILE);
}

/** Parse the [[pack]] array-of-tables in synkro.lock. Tolerant of absence. */
export function readLockfile(repoRoot: string): LockEntry[] {
  const fp = lockPath(repoRoot);
  if (!existsSync(fp)) return [];
  const out: LockEntry[] = [];
  let cur: Partial<LockEntry> | null = null;
  const flush = () => {
    if (cur && cur.ref && cur.version && cur.digest) {
      out.push({
        ref: cur.ref, version: cur.version, digest: cur.digest,
        signature: cur.signature ?? '', signingKeyId: cur.signingKeyId ?? '',
      });
    }
    cur = null;
  };
  for (const rawLine of readFileSync(fp, 'utf-8').split('\n')) {
    const line = rawLine.trim();
    if (!line || line.startsWith('#')) continue;
    if (line === '[[pack]]') { flush(); cur = {}; continue; }
    if (!cur) continue;
    const eq = line.indexOf('=');
    if (eq === -1) continue;
    const key = line.slice(0, eq).trim();
    const val = line.slice(eq + 1).trim().replace(/^["']|["']$/g, '');
    if (key === 'ref') cur.ref = val;
    else if (key === 'version') cur.version = val;
    else if (key === 'digest') cur.digest = val;
    else if (key === 'signature') cur.signature = val;
    else if (key === 'signing_key_id') cur.signingKeyId = val;
  }
  flush();
  return out;
}

export function writeLockfile(repoRoot: string, entries: LockEntry[]): void {
  const sorted = [...entries].sort((a, b) => a.ref.localeCompare(b.ref));
  const body = [
    '# synkro.lock — generated by `synkro sync`. Commit this file.',
    '# Pins each subscribed pack to its verified, attested digest.',
    '',
    ...sorted.flatMap((e) => ([
      '[[pack]]',
      `ref = "${e.ref}"`,
      `version = "${e.version}"`,
      `digest = "${e.digest}"`,
      `signature = "${e.signature}"`,
      `signing_key_id = "${e.signingKeyId}"`,
      '',
    ])),
  ].join('\n');
  writeFileSync(lockPath(repoRoot), body, 'utf-8');
}
